<script lang="ts">
	import Icon from '../../../basic/Icon.svelte';
	import IconButton from '../../../basic/buttons/IconButton.svelte';
	import { contextMenuState } from '../../../basic/contextmenu/ContextMenuStore';
	import { confirmState } from '../../../basic/dialog/ConfirmStore';
	import { deepCopy } from '../../../helper/Copy';
	import { addNotification } from '../../../notification/Notificationstore';
	import { transformHTMLToString } from '../../../pdf/TransformFunctions';
	import { documentDragStore } from '../../../stores/DocumentViewStore.svelte';
	import { editFolderStore, type FolderListStore } from '../../../stores/FolderStore.svelte';
	import { handleStoreError } from '../../../stores/GeneralStore';
	import type { MassPatchDocumentInformation } from '../../../types/DocumentTypes';
	import type { Folder, PatchFolder } from '../../../types/FolderTypes';
	import type { FilterOption } from '../../../types/ListTypes';
	import { _ } from 'svelte-i18n';

	interface Props {
		store: FolderListStore;
		item: Folder;
		selected: boolean;
		filterOptions: FilterOption[];
		onClick: (e: MouseEvent) => void;
		onChildDeleted: (item: Folder) => void;
		onRequestContextmenu: (e: MouseEvent) => void;
	}

	let {
		store,
		item,
		selected,
		filterOptions,
		onClick,
		onChildDeleted,
		onRequestContextmenu
	}: Props = $props();

	let shortNameHover = $derived(transformHTMLToString(item.shortName));
	let nameHover = $derived(transformHTMLToString(item.name));

	async function openContextMenu(e: MouseEvent) {
		e.stopPropagation();
		const menuItems = [
			{
				title: $_('general.lbl_edit'),
				callbackFunction: () => {
					editFolderStore.set(deepCopy(item));
				}
			},
			{
				title: item.archived
					? $_('folderview.edit_context.unarchive')
					: $_('folderview.edit_context.archive'),
				callbackFunction: () => {
					handleToggleArchiveStateOfFolder();
				}
			},
			{
				title: $_('general.lbl_delete'),
				danger: true,
				callbackFunction: () => {
					$confirmState = {
						bodyText: $_('folderview.lbl_folder_delete_modal'),
						successCallback: handleDeleteFolder,
						warningText: $_('folderview.lbl_folder_delte_warning'),
						securityCheckText: item.shortName,
						alternativeOptionButtonText: item.archived
							? null
							: $_('folderview.edit_context.archive'),
						alternativeOptionCallback: item.archived ? null : handleToggleArchiveStateOfFolder,
						acceptButtonText: $_('general.lbl_delete')
					};
				}
			}
		];

		//Show the context menu
		$contextMenuState = {
			x: e.clientX,
			y: e.clientY,
			menuItems: menuItems
		};
	}

	async function handleDeleteFolder() {
		await store.deleteFolderStore(item.id);
		onChildDeleted(item);
	}

	async function handleOnDrop() {
		//Ignore null
		if ($documentDragStore == null) return;

		//Get the set of objects
		const set = $documentDragStore;
		//If the set is empty return
		if (set.size <= 0) return;

		//Create a mass patch array
		const patchArray: MassPatchDocumentInformation[] = [];
		//Map the set values to the patch objects
		for (let id of set.values()) {
			patchArray.push({
				documentInformationId: id,
				patchDocumentInformation: {
					fkFolder: item.id
				}
			});
		}

		await store.massMoveDocuments(patchArray);

		//Set the drag element back to an empty set
		$documentDragStore = null;
	}

	async function handleToggleArchiveStateOfFolder() {
		let request = { archived: !item.archived } as PatchFolder;

		try {
			const archivedFilter = filterOptions.find((option) => option.key === 'archived');
			const archivedShowed = (archivedFilter && archivedFilter.value != null) || false;

			await store.patchArchiveState(archivedShowed, item.id, request);

			addNotification({
				message: request.archived
					? $_('folderview.lbl_archive_folder_success')
					: $_('folderview.lbl_unarchive_folder_success'),
				type: 'success'
			});
		} catch (error) {
			handleStoreError('folderview.err_patch_folder', error);
		}
	}
</script>

<button
	ondragover={(e) => e.preventDefault()}
	ondrop={handleOnDrop}
	onclick={onClick}
	class:selected
	class="bg-white dark:bg-dark-500 rounded-[5px] text-left px-2 py-2
    relative border-2 border-transparent hover:border-secondary-200
     dark:hover:border-secondary-500 outline-secondary-200 selected:border-secondary-200 dark:selected:border-secondary-500"
	oncontextmenu={(e) => {
		e.preventDefault();
		onRequestContextmenu(e);
		openContextMenu(e);
	}}
	class:drop-zone-highlight={$documentDragStore && $documentDragStore.size > 0}
>
	<div class="grid grid-cols-[32px_1fr_auto] items-center gap-4">
		<div class="p-2">
			{#if item.archived}
				<Icon name="box" classInfo="dark:fill-white" />
			{:else}
				<Icon
					name={selected ? 'folder-open' : 'folder'}
					classInfo="dark:fill-white selected:fill-secondary-200 dark:selected:fill-secondary-500 {selected
						? 'selected'
						: ''}"
				/>
			{/if}
		</div>

		<div class="overflow-x-auto">
			<p title={shortNameHover} class="truncate font-bold">
				{item.shortName}
			</p>

			<div title={nameHover} class="truncate font-bold">
				{item.name}
			</div>
		</div>

		<div class="flex items-center justify-end">
			{#if item.memo}
				<Icon name="memo" size="small" classInfo="ml-2 dark:fill-white" hoverText={item.memo} />
			{/if}

			{#if selected}
				<IconButton
					iconName="ellipsis-h"
					size="small"
					showCircle={false}
					onClick={openContextMenu}
				/>
			{/if}
		</div>
	</div>
</button>
